import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { StudyDemoTutorReplay } from '@/pages/try/studyExperience/StudyDemoTutorReplay';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  previewNoteTutor,
  startNoteTutor,
  type NoteTutorPreview,
  type NoteTutorStart,
  type StudyNote,
} from './studyNotesApi';

const schema = z.object({
  document_id: z.string().min(1),
  goal: z.enum(['understand', 'homework', 'revise']),
  include_words: z.boolean(),
});
type Form = z.infer<typeof schema>;
function LiveStudyNoteTutor({
  note,
  enabled,
  onPendingChange,
  onOpenSession,
}: {
  note: StudyNote;
  enabled: boolean;
  onPendingChange: (pending: boolean) => void;
  onOpenSession: (id: string) => void;
}) {
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      document_id: note.pages[0]?.document_id ?? '',
      goal: 'understand',
      include_words: false,
    },
  });
  const [range, setRange] = useState<{ from: number; to: number } | null>(null);
  const [preview, setPreview] = useState<{
    value: NoteTutorPreview;
    selection: NoteTutorStart['selection'];
    fingerprint: string;
  } | null>(null);
  const [pending, setPending] = useState<NoteTutorStart | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState('');
  const values = form.watch();
  const fingerprint = JSON.stringify({ values, range, version: note.version });
  async function prepare(values: Form) {
    if (!enabled || inFlight.current) return;
    if (
      values.include_words &&
      (!range || range.to <= range.from || range.to - range.from > 3000)
    ) {
      setError('Select a phrase of up to 3000 characters from your saved words.');
      return;
    }
    const selection = {
      expected_version: note.version,
      document_id: values.document_id,
      goal: values.goal,
      text_from: values.include_words ? range!.from : null,
      text_to: values.include_words ? range!.to : null,
    };
    inFlight.current = true;
    setBusy(true);
    setError('');
    setPreview(null);
    try {
      setPreview({ value: await previewNoteTutor(note.id, selection), selection, fingerprint });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Tutor context could not be checked.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function openTutor() {
    if (
      inFlight.current ||
      (!pending &&
        (!enabled ||
          !preview ||
          preview.fingerprint !== fingerprint ||
          preview.value.tutor.status !== 'published'))
    )
      return;
    const request = pending ?? { request_id: crypto.randomUUID(), selection: preview!.selection };
    inFlight.current = true;
    setPending(request);
    onPendingChange(true);
    setBusy(true);
    setError('');
    try {
      const session = await startNoteTutor(note.id, request);
      setPending(null);
      onPendingChange(false);
      onOpenSession(session.id);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Tutor could not open.');
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500
      ) {
        setPending(null);
        onPendingChange(false);
        setPreview(null);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  const allowed = enabled && note.pages.length > 0 && Boolean(note.term_id && note.subject_key);
  return (
    <section
      className="mt-6 rounded-3xl border border-hairline bg-surface p-5"
      aria-label="Tutor from my notes"
    >
      <h2 className="text-lg font-extrabold text-ink">Learn with your subject Tutor</h2>
      <p className="mt-2 text-sm text-ink-soft">
        Choose one original page. Your reading trim is not sent. Include your own words only when
        you select them.
      </p>
      {!enabled && !pending && (
        <p className="mt-3 text-sm">Save changes and confirm pending requests first.</p>
      )}
      {(!note.term_id || !note.subject_key) && (
        <p className="mt-3 text-sm">Save a Term and subject to use its reviewed Tutor.</p>
      )}
      {note.pages.length === 0 && (
        <p className="mt-3 text-sm">Add an original photo for this source-based Tutor.</p>
      )}
      <form className="mt-4" onSubmit={form.handleSubmit(prepare)}>
        <fieldset
          disabled={!allowed || busy || Boolean(pending)}
          className="grid gap-3 sm:grid-cols-2"
        >
          <label className="text-sm font-bold">
            Tutor source page
            <select
              aria-label="Tutor source page"
              className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
              {...form.register('document_id')}
            >
              {note.pages.map((page, index) => (
                <option key={page.document_id} value={page.document_id}>
                  Page {index + 1} · Original photo
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold">
            Learning goal
            <select
              aria-label="Learning goal"
              className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
              {...form.register('goal')}
            >
              <option value="understand">Explain this</option>
              <option value="homework">Check my understanding</option>
              <option value="revise">Help me practise</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm font-bold sm:col-span-2">
            <input
              type="checkbox"
              {...form.register('include_words')}
              disabled={!note.body || !allowed || busy || Boolean(pending)}
            />
            Include selected words from my notes
          </label>
          {values.include_words && (
            <label className="text-sm font-bold sm:col-span-2">
              Select my saved words
              <textarea
                readOnly
                value={note.body}
                aria-label="Select my saved words"
                onSelect={(event) => {
                  const t = event.currentTarget;
                  setRange({ from: t.selectionStart, to: t.selectionEnd });
                }}
                className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-sm font-normal"
                rows={4}
              />
            </label>
          )}
        </fieldset>
        <button className="btn-pill-secondary mt-4" disabled={!allowed || busy || Boolean(pending)}>
          {busy && !pending ? 'Checking selection…' : 'Preview Tutor context'}
        </button>
      </form>
      {preview && preview.fingerprint === fingerprint && (
        <div className="mt-4 rounded-2xl bg-canvas-pure p-4" aria-label="Tutor context preview">
          <p className="font-bold">
            {preview.value.tutor.label} · Page {preview.value.page_number}
          </p>
          <p className="mt-2 text-xs text-slate2">
            Saved note version {preview.value.notebook_version}. Other pages and private annotations
            are excluded.
          </p>
          <p className="mt-3 text-sm font-bold">Selected learner-authored words</p>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {preview.value.learner_text || 'None selected'}
          </p>
          {preview.value.truncated && (
            <p className="mt-2 text-xs text-slate2">
              The Tutor context limit shortens your selection to the words shown here.
            </p>
          )}
          {preview.value.tutor.status !== 'published' && (
            <p className="mt-3 text-sm" role="status">
              This subject Tutor needs a published, reviewed preset. Your notes remain saved.
            </p>
          )}
          <p className="mt-3 text-xs text-slate2">
            Opening does not run AI. Tutor shows the Stars price before you send a question. Source
            text and your notes share its context limit.
          </p>
          <button
            type="button"
            className="btn-pill-primary mt-4"
            disabled={busy || preview.value.tutor.status !== 'published' || (!enabled && !pending)}
            onClick={() => void openTutor()}
          >
            {busy && pending
              ? 'Opening Tutor…'
              : pending
                ? 'Retry opening Tutor'
                : 'Confirm and open Tutor'}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm">
          {error}
          {pending && ' The result is unconfirmed. Retry the same request.'}
        </p>
      )}
    </section>
  );
}

export function StudyNoteTutor(props: Parameters<typeof LiveStudyNoteTutor>[0]) {
  if (isLocalStudyExperience())
    return (
      <details className="mt-4 rounded-2xl border border-hairline bg-canvas-pure p-3">
        <summary className="min-h-11 cursor-pointer font-bold text-ink">
          View prepared Tutor explanation
        </summary>
        <StudyDemoTutorReplay
          key={props.note.id}
          initialKey={
            props.note.topic === 'Geometry'
              ? 'cone'
              : props.note.topic === 'Algebra'
                ? 'linear'
                : 'fraction'
          }
        />
      </details>
    );
  return <LiveStudyNoteTutor {...props} />;
}
